// useCESDK.js
import { useState, useEffect, useCallback, useRef } from 'react';
import CreativeEditorSDK from '@cesdk/cesdk-js';
import supabase from '../utils/supabaseUtils';

export function useCESDK() {
  const cesdkRef = useRef(null);
  const initializationAttemptedRef = useRef(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [assetSources, setAssetSources] = useState({
    backgrounds: false,
    athletes: false,
    templates: false
  });

  // Initialize the editor
  const initialize = useCallback(async (container, options = {}) => {
    if (!container) {
      throw new Error('Container element is required for initialization');
    }
    
    // Prevent multiple initialization attempts
    if (initializationAttemptedRef.current) {
      console.warn("Initialization already attempted, skipping");
      
      // If we already have an initialized instance, return it
      if (cesdkRef.current) {
        return cesdkRef.current;
      }
      
      // Otherwise, wait for initialization or throw an error
      throw new Error('Initialization already in progress or failed');
    }
    
    initializationAttemptedRef.current = true;
    
    try {
      setStatus('initializing');
      
      // Configure CESDK with settings from the known working version
      const config = {
        license: import.meta.env.VITE_CESDK_LICENSE,
        baseURL: "/cesdk-assets",
        theme: "dark", // Use the built-in dark theme
        role: "Creator",
        ui: { 
          elements: { 
            view: 'advanced', // 'advanced' was used in the working version
            panels: { settings: true },
            dock: { 
              enabled: true,
              placeholderOverlay: false
            },
            libraries: {
              enabled: true
            },
            navigation: {
              enabled: true
            },
            pages: {
              enabled: true
            }
          }
        },
        callbacks: {
          onUpload: 'local', // Set to 'local' to enable the upload button immediately
          onRendered: () => {
            // Called when the editor has rendered - use this to update background color
            console.log("CESDK has rendered, setting background color");
            try {
              // Find all canvas elements
              const canvasEls = document.querySelectorAll('.ubq-editor-view__canvas');
              if (canvasEls.length > 0) {
                canvasEls.forEach(el => {
                  el.style.backgroundColor = '#4F6273';
                });
              }
            } catch (e) {
              console.warn("Error setting canvas background in onRendered:", e);
            }
          }
        },
        ...options
      };

      if (options.userId) {
        config.userId = options.userId;
      }

      if (options.callbacks) {
        // Merge callbacks, don't override completely
        config.callbacks = {
          ...config.callbacks,
          ...options.callbacks
        };
      }
      
      console.log("Initializing CESDK with config:", config);
      const instance = await CreativeEditorSDK.create(container, config);
      console.log("CESDK initialized successfully", instance);

      // Add default asset sources with local baseURL
      instance.engine.addDefaultAssetSources({
        baseURL: `${window.location.origin}/cesdk-assets/imglyAssets`
      });

      // Apply custom background color for the editor
      if (instance && instance.ui) {
        try {
          // Inject CSS styles for the editor
          const injectStyles = () => {
            // Check if our style tag already exists
            let styleTag = document.getElementById('cesdk-custom-styles');
            if (!styleTag) {
              // Create a style tag if it doesn't exist
              styleTag = document.createElement('style');
              styleTag.id = 'cesdk-custom-styles';
              document.head.appendChild(styleTag);
            }
            
            // Define the CSS to inject
            const css = `
              /* Our custom class to identify the color */
              .playmaker-blue-bg {
                background-color: #4F6273 !important;
              }
              
              /* Target by role */
              [data-ubq-role="editor"],
              [data-ubq-role="canvas"],
              [data-ubq-role="canvas-container"] {
                background-color: #4F6273 !important;
              }
            
              :root {
                --cesdk-background-color: #4F6273 !important;
              }
              
              /* Direct color overrides for dark theme */
              .ubq-dark {
                --color-background: 79, 98, 115 !important;
              }
              
              /* Target all main editor containers with high specificity */
              body .ubq-layout.ubq-dark,
              body .ubq-editor-view__canvas-container.ubq-dark,
              body .ubq-dock-container.ubq-dark,
              body .ubq-editor-view__canvas-wrapper.ubq-dark,
              body .ubq-dock.ubq-dark {
                background-color: #4F6273 !important;
              }
              
              /* Target the specific dark theme containers */
              body .ubq-dark .ubq-editor-view__canvas-container,
              body .ubq-dark .ubq-dock-container,
              body .ubq-dark .ubq-editor-view__canvas-wrapper,
              body .ubq-dark .ubq-dock {
                background-color: #4F6273 !important;
              }
              
              /* Target the canvas background color */
              body .ubq-editor-view__canvas {
                background-color: #4F6273 !important;
              }
            `;
            
            // Set the CSS content
            styleTag.textContent = css;
            console.log("Injected custom background styles for editor");
          };
          
          // Inject styles immediately
          injectStyles();
          
          // Also try the direct property setting approach
          const setEditorBackground = () => {
            const color = '#4F6273';
            
            // Target all relevant editor elements with our background color
            const selectors = [
              '.ubq-layout',
              '.ubq-editor-view__canvas-container',
              '.ubq-dock-container',
              '.ubq-editor-view__canvas-wrapper',
              '.ubq-dock',
              '.ubq-editor-view__canvas'
            ];
            
            selectors.forEach(selector => {
              const elements = document.querySelectorAll(selector);
              elements.forEach(el => {
                el.style.backgroundColor = color;
                // Add our custom class for additional styling control
                el.classList.add('playmaker-blue-bg');
              });
            });
            
            // Also try setting CSS variables on the layout
            const layout = document.querySelector('.ubq-layout');
            if (layout) {
              layout.style.setProperty('--color-background', '79, 98, 115', 'important');
            }
            
            console.log("Applied custom background color to editor elements");
          };
          
          // Try immediately and after a delay to ensure DOM is loaded
          setEditorBackground();
          setTimeout(setEditorBackground, 500);
          setTimeout(setEditorBackground, 1000);
          
          // Also set up a MutationObserver to catch when the layout is added to DOM
          const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              if (mutation.addedNodes.length) {
                if (document.querySelector('.ubq-layout')) {
                  setEditorBackground();
                  // Don't disconnect yet in case other elements are still loading
                  setTimeout(() => {
                    setEditorBackground();
                    observer.disconnect();
                  }, 1000);
                  break;
                }
              }
            }
          });
          
          // Start observing
          observer.observe(document.body, { childList: true, subtree: true });
          
          // Clean up after a reasonable time
          setTimeout(() => observer.disconnect(), 10000);
        } catch (e) {
          console.warn("Unable to customize editor background:", e);
        }
      }

      // Store in ref for stable cleanup
      cesdkRef.current = instance;
      
      // Make it available globally for debugging if needed
      window.cesdk = instance;
      
      // Additional debug info
      if (instance.engine && instance.ui) {
        console.log("Engine API available:", !!instance.engine);
        console.log("UI API available:", !!instance.ui);
        console.log("Asset API available:", !!(instance.engine && instance.engine.asset));
        
        // Try to log which blocks are visible in the dock
        try {
          if (instance.ui.dock) {
            console.log("Dock API available");
          } else {
            console.warn("Dock API not available in UI");
          }
        } catch (e) {
          console.warn("Error checking dock API:", e);
        }
      }
      
      setStatus('ready');
      return instance;
    } catch (err) {
      console.error('CESDK initialization error:', err);
      setError(err);
      setStatus('error');
      initializationAttemptedRef.current = false; // Reset on error to allow retry
      throw err;
    }
  }, []);

  // Clean up the editor
  const cleanup = useCallback(() => {
    if (cesdkRef.current) {
      try {
        // Properly dispose of the instance
        cesdkRef.current.dispose();
        // Clear the reference
        cesdkRef.current = null;
        // Clear global reference
        window.cesdk = null;
        // Reset state
        setStatus('idle');
        setAssetSources({
          backgrounds: false,
          athletes: false,
          templates: false
        });
        // Reset initialization flag
        initializationAttemptedRef.current = false;
      } catch (err) {
        console.error('Error during CESDK cleanup:', err);
      }
    }
  }, []);

  // Get all placeholders in a scene
  const getPlaceholders = useCallback(() => {
    const cesdk = cesdkRef.current;
    if (!cesdk) {
      throw new Error('CESDK not initialized');
    }

    try {
      const scene = cesdk.engine.scene.get();
      if (!scene) {
        return [];
      }
      
      const getAllBlocksInScene = (scene) => {
        const result = [];
        
        // Get all pages
        const pages = cesdk.engine.block.getChildren(scene);
        
        // Process each page
        for (const page of pages) {
          if (!cesdk.engine.block.isValid(page)) continue;
          
          const pageChildren = cesdk.engine.block.getChildren(page);
          for (const child of pageChildren) {
            if (!cesdk.engine.block.isValid(child)) continue;
            result.push(child);
            
            // Add grandchildren (for nested blocks)
            try {
              const grandchildren = cesdk.engine.block.getChildren(child);
              for (const grandchild of grandchildren) {
                if (cesdk.engine.block.isValid(grandchild)) {
                  result.push(grandchild);
                }
              }
            } catch (e) {
              // Ignore errors on getting children
            }
          }
        }
        
        return result;
      };
      
      const blocks = getAllBlocksInScene(scene);
      const placeholders = [];
      
      for (const blockId of blocks) {
        try {
          if (!cesdk.engine.block.isValid(blockId)) continue;
          
          const name = cesdk.engine.block.getName(blockId) || "(unnamed)";
          const type = cesdk.engine.block.getType(blockId);
          
          // Check various ways to identify placeholders
          let isPlaceholder = false;
          
          // Method 1: Check if block name is a known placeholder name
          if (name === 'Person' || name === 'Athlete' || name === 'Placeholder') {
            isPlaceholder = true;
          }
          
          // Method 2: Check if block has placeholder metadata
          try {
            const placeholderMeta = cesdk.engine.block.getMetadata(blockId, "isPlaceholder", "false");
            if (placeholderMeta === "true") {
              isPlaceholder = true;
            }
          } catch (e) {
            // Ignore metadata errors
          }
          
          // Method 3: Check for placeholder API support
          try {
            if (cesdk.engine.block.isPlaceholderEnabled && 
                cesdk.engine.block.isPlaceholderEnabled(blockId)) {
              isPlaceholder = true;
            }
          } catch (e) {
            // Ignore API errors
          }
          
          if (isPlaceholder) {
            // Determine fill type and current content
            let fillType = "";
            let defaultContent = "";
            
            try {
              const fill = cesdk.engine.block.getFill(blockId);
              if (cesdk.engine.block.isValid(fill)) {
                fillType = cesdk.engine.block.getType(fill);
                
                // For image fills, try to get the current image URL
                if (fillType === "//ly.img.ubq/fill/image") {
                  try {
                    // Try different ways to get the image URL
                    try {
                      const sourceSet = cesdk.engine.block.getSourceSet(fill, "fill/image/sourceSet");
                      if (sourceSet && sourceSet.length > 0) {
                        defaultContent = sourceSet[0].uri;
                      }
                    } catch (e) {
                      try {
                        defaultContent = cesdk.engine.block.getString(fill, "fill/image/imageFileURI");
                      } catch (e2) {
                        // Can't get image URL
                      }
                    }
                  } catch (e) {
                    // Ignore error getting image URL
                  }
                }
              }
            } catch (e) {
              // Ignore errors getting fill
            }
            
            placeholders.push({
              id: blockId,
              name,
              type,
              fillType: fillType || "//ly.img.ubq/fill/color",
              defaultContent
            });
          }
        } catch (err) {
          // Skip blocks with errors
          console.warn(`Error processing block for placeholder detection:`, err);
        }
      }
      
      console.log(`Found ${placeholders.length} placeholders in scene:`, placeholders);
      return placeholders;
    } catch (err) {
      console.error('Error getting placeholders:', err);
      return [];
    }
  }, []);

  // Set placeholder properties
  const setBlockAsPlaceholder = useCallback((blockId, enable = true) => {
    const cesdk = cesdkRef.current;
    if (!cesdk || !cesdk.engine || !cesdk.engine.block) {
      throw new Error('CESDK not initialized properly');
    }

    try {
      if (!cesdk.engine.block.isValid(blockId)) {
        throw new Error('Invalid block ID');
      }

      const blockName = cesdk.engine.block.getName(blockId) || "Placeholder";
      
      // Try to use placeholder APIs if supported
      try {
        if (cesdk.engine.block.supportsPlaceholderBehavior &&
            cesdk.engine.block.supportsPlaceholderBehavior(blockId)) {
          cesdk.engine.block.setPlaceholderBehaviorEnabled(blockId, enable);
        }
        
        if (cesdk.engine.block.setPlaceholderEnabled) {
          cesdk.engine.block.setPlaceholderEnabled(blockId, enable);
        }
        
        if (cesdk.engine.block.supportsPlaceholderControls &&
            cesdk.engine.block.supportsPlaceholderControls(blockId)) {
          cesdk.engine.block.setPlaceholderControlsOverlayEnabled(blockId, enable);
          cesdk.engine.block.setPlaceholderControlsButtonEnabled(blockId, enable);
        }
      } catch (e) {
        console.warn("Placeholder API not fully supported for this block:", e);
      }
      
      // Always set metadata as a fallback
      try {
        cesdk.engine.block.setMetadata(blockId, "isPlaceholder", enable ? "true" : "false");
        cesdk.engine.block.setMetadata(blockId, "placeholderName", blockName);
      } catch (metaErr) {
        console.warn("Could not set placeholder metadata:", metaErr);
      }
      
      return true;
    } catch (err) {
      console.error('Error setting placeholder properties:', err);
      return false;
    }
  }, []);

  // Create a blank scene
  const createBlankScene = useCallback(async (primaryImageUrl = null) => {
    const cesdk = cesdkRef.current;
    if (!cesdk) {
      throw new Error('CESDK not initialized');
    }

    try {
      console.log("Creating blank scene...");
      
      let scene;
      if (cesdk.engine && cesdk.engine.scene) {
        // Try to get existing scene or create a new one
        scene = cesdk.engine.scene.get() || await cesdk.engine.scene.create();
      } else {
        throw new Error("Scene API not available");
      }

      if (cesdk.engine && cesdk.engine.block) {
        // Create a new page
        const page = cesdk.engine.block.create('page');
        cesdk.engine.block.setWidth(page, 1920);
        cesdk.engine.block.setHeight(page, 1080);
        
        // Create background block
        const background = cesdk.engine.block.create('graphic');
        cesdk.engine.block.setName(background, 'Background');
        cesdk.engine.block.setWidth(background, 1920);
        cesdk.engine.block.setHeight(background, 1080);
        cesdk.engine.block.setPositionX(background, 0);
        cesdk.engine.block.setPositionY(background, 0);
        
        // Create and set shape for background
        const bgShapeId = cesdk.engine.block.createShape('rect');
        cesdk.engine.block.setShape(background, bgShapeId);
        
        // Create and set fill for background
        const bgFillId = cesdk.engine.block.createFill('color');
        cesdk.engine.block.setColor(bgFillId, 'fill/color/value', { r: 1, g: 1, b: 1, a: 1 });
        cesdk.engine.block.setFill(background, bgFillId);
        
        // Create Person placeholder as a graphic block
        const person = cesdk.engine.block.create('graphic');
        cesdk.engine.block.setName(person, 'Person');
        cesdk.engine.block.setWidth(person, 1080);
        cesdk.engine.block.setHeight(person, 720);
        cesdk.engine.block.setPositionX(person, 420);
        cesdk.engine.block.setPositionY(person, 180);
        
        // Create and set shape for person
        const personShapeId = cesdk.engine.block.createShape('rect');
        cesdk.engine.block.setShape(person, personShapeId);
        
        // Create a placeholder fill (we'll use a color fill by default)
        const personFillId = cesdk.engine.block.createFill('color');
        cesdk.engine.block.setColor(personFillId, 'fill/color/value', { r: 0.9, g: 0.9, b: 0.9, a: 1 });
        cesdk.engine.block.setFill(person, personFillId);
        
        // Try to set placeholder properties if supported
        try {
          if (cesdk.engine.block.supportsPlaceholderBehavior && 
              cesdk.engine.block.supportsPlaceholderBehavior(person)) {
            cesdk.engine.block.setPlaceholderBehaviorEnabled(person, true);
          }
          
          if (cesdk.engine.block.setPlaceholderEnabled) {
            cesdk.engine.block.setPlaceholderEnabled(person, true);
          }
          
          if (cesdk.engine.block.supportsPlaceholderControls && 
              cesdk.engine.block.supportsPlaceholderControls(person)) {
            cesdk.engine.block.setPlaceholderControlsOverlayEnabled(person, true);
            cesdk.engine.block.setPlaceholderControlsButtonEnabled(person, true);
          }
        } catch (e) {
          // Fallback to metadata if placeholder APIs don't work
          try {
            cesdk.engine.block.setMetadata(person, 'isPlaceholder', 'true');
            cesdk.engine.block.setMetadata(person, 'placeholderName', 'Person');
          } catch (metaErr) {
            console.warn("Could not set placeholder properties:", e, metaErr);
          }
        }
        
        // Add blocks to page and page to scene
        cesdk.engine.block.appendChild(page, background);
        cesdk.engine.block.appendChild(page, person);
        cesdk.engine.block.appendChild(scene, page);
      }
      
      console.log("Blank scene created successfully");
      return scene;
    } catch (err) {
      console.error('Error creating blank scene:', err);
      throw err;
    }
  }, []);

  // Register asset sources
  const registerAssetSources = useCallback(async () => {
    const cesdk = cesdkRef.current;
    if (!cesdk || !cesdk.engine || !cesdk.engine.asset) {
      console.warn("CESDK asset API not available yet, skipping asset source registration");
      return false;
    }

    console.log("Registering asset sources...");
    
    try {
      // Add backgrounds source
      console.log("Registering backgrounds source");
      cesdk.engine.asset.addSource({
        id: 'supabase-backgrounds',
        findAssets: async () => {
          try {
            const { data: folderData, error: folderError } = await supabase.storage
              .from('playmakerpics-images')
              .list('backgrounds', { limit: 100, offset: 0 });
            
            if (folderError) {
              console.error("Error listing background folders:", folderError);
              throw folderError;
            }
            
            let allAssets = [];
            // Only consider entries without extensions as folders (like 'Baseball', 'Soccer', etc.)
            const subfolders = folderData.filter(f => 
              !f.name.includes('.') && f.name !== '.emptyFolderPlaceholder'
            );
            
            console.log(`Found ${subfolders.length} background category folders:`, subfolders.map(f => f.name));
            
            // Process each sport subfolder
            for (const folder of subfolders) {
              // Create a proper category name - capitalize the folder name
              const categoryName = folder.name.charAt(0).toUpperCase() + folder.name.slice(1);
              
              const subPath = `backgrounds/${folder.name}`;
              const { data: fileData, error: fileError } = await supabase.storage
                .from('playmakerpics-images')
                .list(subPath, { limit: 100, offset: 0 });
              
              if (fileError) {
                console.error(`Error listing files in ${subPath}:`, fileError);
                continue;
              }
              
              console.log(`Found ${fileData.length} files in background category ${folder.name}`);
              
              // Only include image files
              const subAssets = fileData
                .filter(file => /\.(jpg|jpeg|png|gif)$/i.test(file.name))
                .map(file => {
                  const encodedFileName = encodeURIComponent(file.name);
                  const fullPath = `${subPath}/${encodedFileName}`;
                  const { data: urlData } = supabase.storage
                    .from('playmakerpics-images')
                    .getPublicUrl(fullPath);
                    
                  if (!urlData || !urlData.publicUrl) {
                    console.warn(`Could not get public URL for ${fullPath}`);
                    return null;
                  }
                    
                  const uri = `${urlData.publicUrl}?t=${Date.now()}`;
                  
                  return {
                    id: `bg-${folder.name}-${file.name.replace(/\//g, '-')}`,
                    label: file.name.split('.')[0] || "Background Image",
                    kind: 'image',
                    meta: {
                      uri,
                      thumbUri: uri,
                      blockType: '//ly.img.ubq/graphic',
                      fillType: '//ly.img.ubq/fill/image',
                      shapeType: '//ly.img.ubq/shape/rect',
                      width: 1920,
                      height: 1080,
                      mimeType: file.name.match(/\.(png|gif)$/i) ? "image/png" : "image/jpeg",
                      cacheBuster: Date.now(),
                      category: categoryName,  // Set the category to the folder name
                    },
                  };
                })
                .filter(asset => asset !== null); // Remove null entries
              
              allAssets = allAssets.concat(subAssets);
            }
            
            // Also check for images directly in the backgrounds folder (uncategorized)
            const rootImages = folderData.filter(f => /\.(jpg|jpeg|png|gif)$/i.test(f.name));
            if (rootImages.length > 0) {
              console.log(`Found ${rootImages.length} uncategorized backgrounds`);
              
              const uncategorizedAssets = rootImages.map(file => {
                try {
                  const encodedFileName = encodeURIComponent(file.name);
                  const path = `backgrounds/${encodedFileName}`;
                  const { data: urlData } = supabase.storage
                    .from('playmakerpics-images')
                    .getPublicUrl(path);
                    
                  if (!urlData || !urlData.publicUrl) {
                    console.warn(`Could not get public URL for ${path}`);
                    return null;
                  }
                    
                  const uri = `${urlData.publicUrl}?t=${Date.now()}`;
                  
                  return {
                    id: `bg-root-${file.name.replace(/\//g, '-')}`,
                    label: file.name.split('.')[0] || "Background Image",
                    kind: 'image',
                    meta: {
                      uri,
                      thumbUri: uri,
                      blockType: '//ly.img.ubq/graphic',
                      fillType: '//ly.img.ubq/fill/image',
                      shapeType: '//ly.img.ubq/shape/rect',
                      width: 1920,
                      height: 1080,
                      mimeType: file.name.match(/\.(png|gif)$/i) ? "image/png" : "image/jpeg",
                      cacheBuster: Date.now(),
                      category: "General",  // Use "General" for uncategorized backgrounds
                    },
                  };
                } catch (error) {
                  console.warn(`Error processing file ${file.name}:`, error);
                  return null;
                }
              }).filter(asset => asset !== null);
              
              allAssets = allAssets.concat(uncategorizedAssets);
            }
            
            // Add a test background if no assets found
            if (allAssets.length === 0) {
              allAssets.push({
                id: 'test-background',
                label: 'Test Background',
                kind: 'image',
                meta: {
                  uri: 'https://picsum.photos/1920/1080',
                  thumbUri: 'https://picsum.photos/200/200',
                  blockType: '//ly.img.ubq/graphic',
                  fillType: '//ly.img.ubq/fill/image',
                  shapeType: '//ly.img.ubq/shape/rect',
                  width: 1920,
                  height: 1080,
                  mimeType: "image/jpeg",
                  category: "Sample"
                },
              });
            }
            
            console.log(`Successfully processed ${allAssets.length} background assets`);
            return { 
              assets: allAssets, 
              total: allAssets.length, 
              currentPage: 0, 
              nextPage: undefined 
            };
          } catch (error) {
            console.error("Error fetching background images:", error);
            return {
              assets: [{
                id: 'test-background',
                label: 'Test Background',
                kind: 'image',
                meta: {
                  uri: 'https://picsum.photos/1920/1080',
                  thumbUri: 'https://picsum.photos/200/200',
                  blockType: '//ly.img.ubq/graphic',
                  fillType: '//ly.img.ubq/fill/image',
                  shapeType: '//ly.img.ubq/shape/rect',
                  width: 1920,
                  height: 1080,
                  mimeType: "image/jpeg",
                  category: "Sample"
                },
              }], 
              total: 1, 
              currentPage: 0, 
              nextPage: undefined
            };
          }
        }
      });
      
      // Add local stickers source from public/cesdk-assets/imglyAssets
      console.log("Registering stickers source");
      cesdk.engine.asset.addSource({
        id: 'local-imgly-stickers',
        findAssets: async () => {
          const stickerFiles = [
            'fire.png',
            'lightning.png',
            'goal.png'
            // Add more filenames here as needed
          ];

          const baseUri = `${window.location.origin}/cesdk-assets/imglyAssets/ly.img.sticker`;
          const assets = stickerFiles.map((file) => {
            const uri = `${baseUri}/${file}?t=${Date.now()}`;
            return {
              id: `sticker-${file}`,
              kind: 'sticker',  // Important: This ensures it appears in the stickers tab
              meta: {
                uri,
                thumbUri: uri,
                blockType: '//ly.img.ubq/graphic',
                fillType: '//ly.img.ubq/fill/image',
                shapeType: '//ly.img.ubq/shape/rect',
                width: 512,
                height: 512,
                mimeType: 'image/png',
                category: 'Custom Stickers',  // Optional: Groups them in a category
                cacheBuster: Date.now()
              }
            };
          });

          console.log(`Found ${assets.length} sticker assets`);
          return {
            assets,
            total: assets.length,
            currentPage: 0,
            nextPage: undefined
          };
        }
      });

      // Add athletes source
      console.log("Registering athletes source");
      cesdk.engine.asset.addSource({
        id: 'supabase-athlete-images',
        findAssets: async () => {
          try {
            // First check if we have categories in subfolders
            const { data: folders, error: folderError } = await supabase.storage
              .from('playmakerpics-images')
              .list('athletes', { limit: 100, offset: 0 });
            
            if (folderError) {
              console.error("Error listing athlete folders:", folderError);
              throw folderError;
            }
            
            const assets = [];
            const categories = new Set(); // Track unique categories
            
            // Check if we have subfolders (directories don't have extensions)
            const subfolders = folders.filter(item => 
              !item.name.includes('.') && item.name !== '.emptyFolderPlaceholder'
            );
            
            // If we have subfolders, use them as categories
            if (subfolders.length > 0) {
              console.log(`Found ${subfolders.length} category folders:`, subfolders.map(f => f.name));
              
              // Process each subfolder as a category
              for (const folder of subfolders) {
                const categoryName = folder.name.charAt(0).toUpperCase() + folder.name.slice(1); // Capitalize
                categories.add(categoryName);
                
                // List files in this category folder
                const { data: files, error: filesError } = await supabase.storage
                  .from('playmakerpics-images')
                  .list(`athletes/${folder.name}`, { limit: 100, offset: 0 });
                  
                if (filesError) {
                  console.warn(`Error listing files in category ${folder.name}:`, filesError);
                  continue;
                }
                
                console.log(`Found ${files.length} files in category ${folder.name}`);
                
                // Process each file in this category
                for (const file of files) {
                  if (!/\.(jpg|jpeg|png|gif)$/i.test(file.name)) continue;
                  
                  try {
                    const encodedFileName = encodeURIComponent(file.name);
                    const path = `athletes/${folder.name}/${encodedFileName}`;
                    const { data: urlData } = supabase.storage
                      .from('playmakerpics-images')
                      .getPublicUrl(path);
                    
                    if (!urlData || !urlData.publicUrl) {
                      console.warn(`Could not get public URL for ${path}`);
                      continue;
                    }
                    
                    // Add cache busting parameter
                    const uri = `${urlData.publicUrl}?t=${Date.now()}`;
                    
                    const asset = {
                      id: `athlete-${folder.name}-${file.name.replace(/\s+/g, '-')}`,
                      label: file.name.split('.')[0] || "Placeholder Image",
                      kind: 'image',
                      meta: {
                        uri,
                        thumbUri: uri,
                        blockType: '//ly.img.ubq/graphic',
                        fillType: '//ly.img.ubq/fill/image',
                        shapeType: '//ly.img.ubq/shape/rect',
                        width: 1080,
                        height: 720,
                        mimeType: /\.(png|gif)$/i.test(file.name) ? "image/png" : "image/jpeg",
                        cacheBuster: Date.now(),
                        category: categoryName, // Add category
                      },
                    };
                    
                    assets.push(asset);
                  } catch (assetErr) {
                    console.warn(`Error processing file ${file.name} in category ${folder.name}:`, assetErr);
                  }
                }
              }
            } else {
              // No subfolders, use root files with default categorization
              const files = folders.filter(item => 
                /\.(jpg|jpeg|png|gif)$/i.test(item.name) && item.name !== '.emptyFolderPlaceholder'
              );
              
              console.log(`Found ${files.length} files in root athletes folder`);
              
              // Try to extract categories from filenames (e.g., "football_player1.jpg" → "Football")
              for (const file of files) {
                try {
                  // Try to extract category from filename
                  let category = "General";
                  const nameParts = file.name.split('_');
                  if (nameParts.length > 1) {
                    category = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
                    categories.add(category);
                  } else {
                    // If no underscore, try to guess from common sports terms
                    const sportsTerms = ["football", "baseball", "soccer", "basketball", "tennis", 
                                        "hockey", "golf", "swimming", "volleyball", "track"];
                    for (const sport of sportsTerms) {
                      if (file.name.toLowerCase().includes(sport)) {
                        category = sport.charAt(0).toUpperCase() + sport.slice(1);
                        categories.add(category);
                        break;
                      }
                    }
                  }
                  
                  const encodedFileName = encodeURIComponent(file.name);
                  const path = `athletes/${encodedFileName}`;
                  const { data: urlData } = supabase.storage
                    .from('playmakerpics-images')
                    .getPublicUrl(path);
                  
                  if (!urlData || !urlData.publicUrl) {
                    console.warn(`Could not get public URL for ${path}`);
                    continue;
                  }
                  
                  // Add cache busting parameter
                  const uri = `${urlData.publicUrl}?t=${Date.now()}`;
                  
                  const asset = {
                    id: `athlete-${file.name.replace(/\s+/g, '-')}`,
                    label: file.name.split('.')[0] || "Placeholder Image",
                    kind: 'image',
                    meta: {
                      uri,
                      thumbUri: uri,
                      blockType: '//ly.img.ubq/graphic',
                      fillType: '//ly.img.ubq/fill/image',
                      shapeType: '//ly.img.ubq/shape/rect',
                      width: 1080,
                      height: 720,
                      mimeType: /\.(png|gif)$/i.test(file.name) ? "image/png" : "image/jpeg",
                      cacheBuster: Date.now(),
                      category: category, // Add category
                    },
                  };
                  
                  assets.push(asset);
                } catch (assetErr) {
                  console.warn(`Error processing file ${file.name}:`, assetErr);
                }
              }
            }
            
            // Add a test athlete if no assets found
            if (assets.length === 0) {
              console.log("No valid placeholder images found, adding test image");
              assets.push({
                id: 'test-athlete',
                label: 'Test Placeholder',
                kind: 'image',
                meta: {
                  uri: 'https://picsum.photos/1080/720',
                  thumbUri: 'https://picsum.photos/200/200',
                  blockType: '//ly.img.ubq/graphic',
                  fillType: '//ly.img.ubq/fill/image',
                  shapeType: '//ly.img.ubq/shape/rect',
                  width: 1080,
                  height: 720,
                  mimeType: "image/png",
                  category: "Sample"
                },
              });
              categories.add("Sample");
            }
            
            console.log(`Successfully processed ${assets.length} placeholder assets in ${categories.size} categories`);
            return { 
              assets, 
              total: assets.length, 
              currentPage: 0, 
              nextPage: undefined 
            };
          } catch (error) {
            console.error("Error fetching athlete images:", error);
            return {
              assets: [{
                id: 'test-athlete',
                label: 'Test Placeholder',
                kind: 'image',
                meta: {
                  uri: 'https://picsum.photos/1080/720',
                  thumbUri: 'https://picsum.photos/200/200',
                  blockType: '//ly.img.ubq/graphic',
                  fillType: '//ly.img.ubq/fill/image',
                  shapeType: '//ly.img.ubq/shape/rect',
                  width: 1080,
                  height: 720,
                  mimeType: "image/png",
                  category: "Sample"
                },
              }], 
              total: 1, 
              currentPage: 0, 
              nextPage: undefined
            };
          }
        }
      });
      
      setAssetSources(prev => ({
        ...prev,
        backgrounds: true,
        athletes: true,
        stickers: true
      }));
      
      console.log("All asset sources registered successfully");
      return true;
    } catch (error) {
      console.error("Error registering asset sources:", error);
      return false;
    }
  }, []);

  // Load templates from Supabase
  const loadTemplates = useCallback(async () => {
    const cesdk = cesdkRef.current;
    if (!cesdk || !cesdk.engine || !cesdk.engine.asset) {
      console.warn("CESDK asset API not available, skipping template loading");
      return [];
    }

    try {
      console.log("Loading templates...");
      
      // Fetch templates from Supabase
      const { data: templates, error } = await supabase
        .from('templates')
        .select('*')
        .order('updated_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      if (!templates || templates.length === 0) {
        console.warn("No templates found");
        return [];
      }
      
      // Add templates to CESDK
      const templateSourceId = 'playmaker-templates';
      
      try {
        cesdk.engine.asset.removeSource(templateSourceId);
      } catch (e) {
        console.log("Source didn't exist yet, creating new");
      }
      
      try {
        cesdk.engine.asset.addLocalSource(templateSourceId);
        
        templates.forEach(template => {
          if (!template.scene_url) {
            console.warn(`Skipping template ${template.id} due to missing scene_url`);
            return;
          }
          
          cesdk.engine.asset.addAssetToSource(templateSourceId, {
            id: `template-${template.id}`,
            label: template.name || "Untitled Template",
            kind: "template",
            meta: {
              uri: template.scene_url,
              thumbUri: template.thumbnail_url || 'https://picsum.photos/400/300',
              category: template.category || "General",
              templateId: template.id // Store the actual template ID for loading
            }
          });
        });
        
        cesdk.engine.asset.assetSourceContentsChanged(templateSourceId);
        
        console.log("Templates loaded successfully");
      } catch (error) {
        console.error("Error adding templates to asset source:", error);
      }
      
      setAssetSources(prev => ({
        ...prev,
        templates: true
      }));
      
      return templates;
    } catch (err) {
      console.error('Error loading templates:', err);
      return [];
    }
  }, []);

  // In the configureUI function, ensure we're adding the "Image Uploads" section correctly
  const configureUI = useCallback(() => {
    const cesdk = cesdkRef.current;
    if (!cesdk || !cesdk.ui) {
      console.warn("CESDK UI not available, skipping UI configuration");
      return false;
    }

    try {
      console.log("Configuring UI...");
      
      // Configure template library
      try {
        cesdk.ui.addAssetLibraryEntry({
          id: "playmaker-templates-entry",
          sourceIds: ["playmaker-templates"],
          sceneMode: "Design",
          previewLength: 5,
          previewBackgroundType: "cover",
          gridBackgroundType: "cover",
          gridColumns: 3,
          callbacks: {
            onAssetSelect: async (asset) => {
              console.log("Template selected:", asset);
              if (asset.kind === "template" && asset.meta?.templateId) {
                if (window.handleTemplateSelection) {
                  await window.handleTemplateSelection(asset.meta.templateId);
                  return true; // Prevent default handling
                }
              }
              return false; // Allow default handling for non-templates
            }
          }
        });
      } catch (e) {
        console.warn("Error adding templates entry:", e);
      }

      // Configure image uploads entry
      try {
        cesdk.ui.addAssetLibraryEntry({
          id: "image-uploads-entry",
          label: "Upload Image",
          sourceIds: ["ly.img.upload"],
          sceneMode: "Design"
        });
        console.log("Added image uploads entry");
      } catch (e) {
        console.warn("Error adding image uploads entry:", e);
      }
      
      // Configure user uploads entry
      try {
        cesdk.ui.addAssetLibraryEntry({
          id: "user-uploads-entry",
          label: "My Uploads",
          sourceIds: ["user-uploads"],
          sceneMode: "Design",
          previewLength: 5,
          previewBackgroundType: "cover",
          gridBackgroundType: "cover",
          gridColumns: 3,
        });
        console.log("Added user uploads entry");
      } catch (e) {
        console.warn("Error adding user uploads entry:", e);
      }

      // Configure backgrounds library with categories
      try {
        cesdk.ui.addAssetLibraryEntry({
          id: "sports-backgrounds-entry",
          label: "Backgrounds",
          sourceIds: ["supabase-backgrounds"],
          sceneMode: "Design",
          previewLength: 5,
          previewBackgroundType: "cover",
          gridBackgroundType: "cover",
          gridColumns: 3,
          gridMode: "category", // Group by sport category
          categoryLabel: "Sport Type", // Make this consistent with other labels
          showSubcategories: true, // Enable subcategories
        });
      } catch (e) {
        console.warn("Error adding backgrounds entry:", e);
      }

      // Configure athletes/placeholders library - now adding it to the Images section
      try {
        cesdk.ui.addAssetLibraryEntry({
          id: "athlete-images-entry",
          label: "Placeholders",
          sourceIds: ["supabase-athlete-images"],
          sceneMode: "Design",
          previewLength: 5,
          previewBackgroundType: "cover",
          gridBackgroundType: "cover",
          gridColumns: 3,
          gridMode: "category",
          categoryLabel: "Sport Type"
        });
      } catch (e) {
        console.warn("Error adding athletes entry:", e);
      }

      // Set dock order - Now removing the separate Placeholders entry and adding
      // the athlete-images-entry to the Images section
      try {
        cesdk.ui.setDockOrder([
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "templates", 
            icon: "@imgly/Template", 
            label: "Templates", 
            entries: ["playmaker-templates-entry"] 
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "backgrounds", 
            icon: "@imgly/Image", 
            label: "Backgrounds", 
            entries: ["sports-backgrounds-entry"] 
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "ly.img.image", 
            icon: "@imgly/Image", 
            label: "Images", 
            // Include athlete-images-entry in the Images dock
            entries: ["image-uploads-entry", "user-uploads-entry", "athlete-images-entry", "ly.img.image"] 
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "ly.img.elements", 
            icon: "@imgly/Library", 
            label: "Elements", 
            entries: ["ly.img.element"] 
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "ly.img.vectorpath", 
            icon: "@imgly/Shapes", 
            label: "Shapes", 
            entries: ["ly.img.vectorpath"] 
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "ly.img.sticker", 
            icon: "@imgly/Sticker", 
            label: "Stickers", 
            entries: ["ly.img.sticker"] 
          },
          { 
            id: "ly.img.assetLibrary.dock", 
            key: "ly.img.text", 
            icon: "@imgly/Text", 
            label: "Text", 
            entries: ["ly.img.text"] 
          },
        ], { editMode: "Transform" });
        console.log("Dock order set successfully");
      } catch (e) {
        console.error("Error setting dock order:", e);
        // Try with a simpler configuration if the full one fails
        try {
          cesdk.ui.setDockOrder([
            { 
              id: "ly.img.text", 
              key: "ly.img.text", 
              icon: "@imgly/Text", 
              label: "Text" 
            },
            { 
              id: "ly.img.assetLibrary.dock", 
              key: "ly.img.image", 
              icon: "@imgly/Image", 
              label: "Images"
            }
          ], { editMode: "Transform" });
          console.log("Fallback dock order set");
        } catch (fallbackError) {
          console.error("Error setting fallback dock order:", fallbackError);
        }
      }

      console.log("UI configured successfully");
      return true;
    } catch (err) {
      console.error('Error configuring UI:', err);
      return false;
    }
  }, []);

  // Load a scene from Supabase by template ID
  const loadSceneFromSupabase = useCallback(async (templateId) => {
    if (!templateId) {
      throw new Error('Template ID is required');
    }
    
    console.log(`Starting to load template with ID: ${templateId}`);
    
    try {
      // Fetch the template details
      const { data: template, error } = await supabase
        .from('templates')
        .select('*')
        .eq('id', templateId)
        .single();
      
      if (error || !template) {
        throw new Error(error?.message || 'Template not found');
      }
      
      if (!template.scene_url) {
        throw new Error('Template has no scene URL');
      }
      
      console.log(`Template found: ${template.name}, loading scene from: ${template.scene_url}`);
      
      // Add cache-busting parameter if needed
      let sceneUrl = template.scene_url;
      if (!sceneUrl.includes('?t=')) {
        sceneUrl += `?t=${Date.now()}`;
      }
      
      // Fetch the scene data
      const response = await fetch(sceneUrl, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Failed to fetch scene: ${response.statusText}`);
      }
      
      let sceneText = await response.text();
      if (!sceneText.startsWith('UBQ')) {
        throw new Error('Invalid scene data format');
      }
      
      console.log(`Scene data loaded successfully, length: ${sceneText.length}`);
      
      // Get the existing CESDK instance
      const cesdk = cesdkRef.current;
      
      // If we have an existing scene, clear it first
      if (cesdk && cesdk.engine && cesdk.engine.scene) {
        const currentScene = cesdk.engine.scene.get();
        if (currentScene) {
          const children = cesdk.engine.block.getChildren(currentScene);
          for (const child of children) {
            try {
              cesdk.engine.block.destroy(child);
            } catch (e) {
              console.warn(`Could not destroy child block: ${e}`);
            }
          }
        }
      }
      
      // Load the scene data directly
      if (cesdk.engine && cesdk.engine.scene && cesdk.engine.scene.loadFromString) {
        await cesdk.engine.scene.loadFromString(sceneText);
        console.log("Scene loaded successfully using engine.scene.loadFromString");
      } else if (cesdk.loadFromString) {
        await cesdk.loadFromString(sceneText);
        console.log("Scene loaded successfully using cesdk.loadFromString");
      } else {
        throw new Error("No method available to load scene from string");
      }
      
      console.log("Template loaded successfully");
      
      return {
        id: templateId,
        name: template.name,
        styleCategory: template.style_category,
        styleSubcategory: template.style_subcategory
      };
    } catch (err) {
      console.error('Error loading scene from Supabase:', err);
      throw err;
    }
  }, []);

  // Save the current scene to Supabase
  const saveSceneToSupabase = useCallback(async (templateName, userId, styleCategory = 'Sports', styleSubcategory = '', templateId = null) => {
    const cesdk = cesdkRef.current;
    if (!cesdk) {
      throw new Error('CESDK not initialized');
    }

    if (!templateName) {
      throw new Error('Template name is required');
    }

    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      console.log(`Saving scene as template: ${templateName}`);
      
      // Get the scene data
      let sceneData;
      if (cesdk.engine && cesdk.engine.scene && cesdk.engine.scene.saveToString) {
        sceneData = await cesdk.engine.scene.saveToString();
      } else if (cesdk.saveToString) {
        sceneData = await cesdk.saveToString();
      } else {
        throw new Error('No method available to save scene to string');
      }
      
      // Create a thumbnail
      let thumbnailBlob;
      if (cesdk.engine && cesdk.engine.block && cesdk.engine.block.export) {
        const scene = cesdk.engine.scene.get();
        thumbnailBlob = await cesdk.engine.block.export(scene, "image/jpeg", { 
          width: 400, 
          height: 300, 
          quality: 0.8 
        });
      } else {
        throw new Error('Cannot create thumbnail');
      }
      
      // Get placeholder data
      const placeholders = getPlaceholders();
      console.log(`Found ${placeholders.length} placeholders to save`);
      
      const placeholderData = placeholders.map(ph => {
        return {
          name: ph.name,
          type: ph.type,
          blockId: ph.id,
          defaultContent: "" // Can be expanded with actual defaults if needed
        };
      });
      
      // Upload scene data
      const sceneFileName = `template-${Date.now()}.ubq`;
      
      // Use 'playmakerpics-images' bucket
      const bucketName = 'playmakerpics-images';
      const folderPath = 'templates';
      
      console.log(`Uploading scene to bucket: ${bucketName}, path: ${folderPath}/${userId}/${sceneFileName}`);
      
      const { data: sceneUpload, error: sceneError } = await supabase.storage
        .from(bucketName)
        .upload(`${folderPath}/${userId}/${sceneFileName}`, new Blob([sceneData], { type: 'application/octet-stream' }), {
          cacheControl: 'no-cache',
          upsert: true
        });
      
      if (sceneError) {
        throw new Error(`Failed to upload scene data: ${sceneError.message}`);
      }
      
      // Get scene URL
      const { data: { publicUrl: sceneUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(`${folderPath}/${userId}/${sceneFileName}`);
      
      // Upload thumbnail
      const thumbnailFileName = `thumbnail-${Date.now()}.jpg`;
      const { data: thumbnailUpload, error: thumbnailError } = await supabase.storage
        .from(bucketName)
        .upload(`${folderPath}/${userId}/${thumbnailFileName}`, thumbnailBlob, {
          contentType: 'image/jpeg',
          cacheControl: 'no-cache',
          upsert: true
        });
      
      if (thumbnailError) {
        throw new Error(`Failed to upload thumbnail: ${thumbnailError.message}`);
      }
      
      // Get thumbnail URL
      const { data: { publicUrl: thumbnailUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(`${folderPath}/${userId}/${thumbnailFileName}`);
      
      // Template data to save
      const templateData = {
        name: templateName,
        user_id: userId,
        scene_url: sceneUrl,
        thumbnail_url: thumbnailUrl,
        placeholder_data: JSON.stringify(placeholderData),
        style_category: styleCategory,
        style_subcategory: styleSubcategory,
        updated_at: new Date().toISOString()
      };
      
      let data;
      
      // If we have a templateId, update the existing template
      if (templateId) {
        console.log(`Updating existing template with ID: ${templateId}`);
        
        const { data: updateData, error: updateError } = await supabase
          .from('templates')
          .update(templateData)
          .eq('id', templateId)
          .select();
        
        if (updateError) {
          throw new Error(`Failed to update template record: ${updateError.message}`);
        }
        
        data = updateData;
      } 
      // Otherwise, create a new template
      else {
        console.log('Creating new template record');
        
        const { data: insertData, error: insertError } = await supabase
          .from('templates')
          .insert(templateData)
          .select();
        
        if (insertError) {
          throw new Error(`Failed to save template record: ${insertError.message}`);
        }
        
        data = insertData;
      }
      
      console.log('Template saved successfully:', data[0].id);
      
      // Reload templates in the asset library
      await loadTemplates();
      
      // Save to exports table
      const { data: exportRecord, error: exportError } = await supabase.from("exports").insert({
        user_id: userId,
        template_id: data[0].id,
        export_url: sceneUrl,
        exported_at: new Date().toISOString(),
        is_saved: true
      }).select().single();
      
      if (exportError) {
        throw new Error(`Failed to save export record: ${exportError.message}`);
      }
      
      return data[0];
    } catch (err) {
      console.error('Error saving scene to Supabase:', err);
      throw err;
    }
  }, [getPlaceholders, loadTemplates]);

  // In useCESDK.js - update the configureImageUpload function
  const configureImageUpload = useCallback(async (userId) => {
    const cesdk = cesdkRef.current;
    if (!cesdk) {
      console.warn("CESDK not initialized, skipping image upload configuration");
      return false;
    }

    if (!userId) {
      console.warn("User ID is required for image upload configuration");
      return false;
    }

    try {
      console.log("Configuring image upload for user:", userId);
      
      // Set up the onUpload handler - now replace the 'local' handler with our custom one
      cesdk.configuration.callbacks = cesdk.configuration.callbacks || {};
      cesdk.configuration.callbacks.onUpload = async (file) => {
        console.log("Upload callback triggered with file:", file);
        
        try {
          // Validate file
          if (!file || !(file instanceof File)) {
            throw new Error('Invalid file object');
          }
          
          // Check file type
          const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
          if (!validTypes.includes(file.type)) {
            throw new Error('Unsupported file type. Please upload a JPG, PNG, or GIF.');
          }
          
          // Check file size (max 10MB)
          const maxSize = 10 * 1024 * 1024; // 10MB
          if (file.size > maxSize) {
            throw new Error('File too large. Maximum size is 10MB.');
          }
          
          // Generate unique filename
          const fileExt = file.name.split('.').pop().toLowerCase();
          const fileName = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
          const filePath = `user_uploads/${userId}/${fileName}`;
          
          console.log(`Uploading file to ${filePath}...`);
          
          // Upload file to Supabase Storage
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('playmakerpics-images')
            .upload(filePath, file, {
              cacheControl: '3600',
              upsert: true,
              contentType: file.type
            });
            
          if (uploadError) {
            throw new Error(`Storage upload failed: ${uploadError.message}`);
          }
          
          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('playmakerpics-images')
            .getPublicUrl(filePath);
            
          // Save to user_uploads table
          const { data: dbData, error: dbError } = await supabase
            .from('user_uploads')
            .insert({
              user_id: userId,
              file_url: publicUrl,
              is_primary: false,
              upload_type: 'editor_upload'
            })
            .select()
            .single();
            
          if (dbError) {
            throw new Error(`Database insert failed: ${dbError.message}`);
          }
          
          console.log("File uploaded successfully:", publicUrl);
          
          // Return image object in the format expected by CESDK
          return {
            id: dbData.id.toString(),
            meta: {
              uri: publicUrl + `?t=${Date.now()}`,
              thumbUri: publicUrl + `?t=${Date.now()}`,
              blockType: '//ly.img.ubq/graphic',
              fillType: '//ly.img.ubq/fill/image',
              shapeType: '//ly.img.ubq/shape/rect',
              width: 1200,
              height: 800,
              mimeType: file.type
            }
          };
        } catch (error) {
          console.error("Upload error:", error);
          if (cesdk.ui && cesdk.ui.showNotification) {
            cesdk.ui.showNotification({
              message: `Upload failed: ${error.message}`,
              type: 'error'
            });
          } else {
            alert(`Upload failed: ${error.message}`);
          }
          return Promise.reject(error);
        }
      };
      
      // Rest of your configureImageUpload function remains the same...
      // Add a source for previously uploaded images
      try {
        const sourceId = 'user-uploads';
        
        // Try to remove the source if it already exists
        try {
          cesdk.engine.asset.removeSource(sourceId);
        } catch (e) {
          // Source might not exist yet, which is fine
        }
        
        // Add the source for user uploads
        cesdk.engine.asset.addSource({
          id: sourceId,
          findAssets: async ({ query, kind, category, page, perPage }) => {
            try {
              console.log("Fetching user uploads with params:", { query, kind, category, page, perPage });
              
              const { data: uploads, error } = await supabase
                .from('user_uploads')
                .select('*')
                .eq('user_id', userId)
                .eq('upload_type', 'editor_upload')
                .order('created_at', { ascending: false });
                
              if (error) {
                console.error("Error fetching user uploads:", error);
                throw error;
              }
              
              console.log(`Found ${uploads?.length || 0} user uploads`);
              
              // Map the database records to the asset format expected by CESDK
              const assets = uploads?.map(upload => {
                // Extract the filename from the URL for the label
                const filename = upload.file_url.split('/').pop().split('?')[0] || "";
                const displayName = filename.replace(/^upload_\d+_[a-z0-9]+_/, '').split('.')[0] || "Upload";
                
                return {
                  id: `upload-${upload.id}`,
                  label: displayName,
                  kind: 'image',
                  meta: {
                    uri: `${upload.file_url}?t=${Date.now()}`,
                    thumbUri: `${upload.file_url}?t=${Date.now()}`,
                    blockType: '//ly.img.ubq/graphic',
                    fillType: '//ly.img.ubq/fill/image',
                    shapeType: '//ly.img.ubq/shape/rect',
                    width: 1200,
                    height: 800,
                    mimeType: upload.file_url.toLowerCase().endsWith('.png') ? 'image/png' : 
                              upload.file_url.toLowerCase().endsWith('.gif') ? 'image/gif' : 
                              'image/jpeg'
                  }
                };
              }) || [];
              
              return {
                assets,
                total: assets.length,
                currentPage: 0,
                nextPage: undefined
              };
            } catch (error) {
              console.error("Error in findAssets for user uploads:", error);
              return {
                assets: [],
                total: 0,
                currentPage: 0,
                nextPage: undefined
              };
            }
          }
        });
        
        console.log("User uploads source configured successfully");
      } catch (error) {
        console.error("Error configuring user uploads source:", error);
      }
      
      return true;
    } catch (err) {
      console.error('Error configuring image upload:', err);
      return false;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Return all functions and state
  return {
    cesdk: cesdkRef.current,
    status,
    error,
    initialize,
    createBlankScene,
    registerAssetSources,
    loadTemplates,
    configureUI,
    loadSceneFromSupabase,
    saveSceneToSupabase,
    getPlaceholders,
    setBlockAsPlaceholder,
    configureImageUpload,
    cleanup,
    isReady: status === 'ready'
  };
}